import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Wheel } from './components/Wheel.tsx';
import B2BLanding from './pages/B2B/index.tsx';
import { PurchasePage } from './pages/Purchase/index.tsx';
import { GlobalStyles } from './styles/GlobalStyles.ts';
import { Logo } from './components/Logo.tsx';
import AdminLogin from './pages/Admin/Login.tsx';
import AdminDashboard from './pages/Admin/Dashboard.tsx';
import BusinessDashboard from './pages/Admin/BusinessDashboard.tsx';
import AdminLayout from './components/AdminLayout.tsx';
import ProtectedAdminRoute from './components/ProtectedAdminRoute.tsx';
import AdminUsers from './pages/Admin/UsersPage.tsx';
import AdminWheels from './pages/Admin/WheelsPage.tsx';
import AdminBusinessUsers from './pages/Admin/BusinessUsersPage.tsx';
import AdminSettings from './pages/Admin/SettingsPage.tsx';
import AdminsPage from './pages/Admin/AdminsPage.tsx';
import DemoRequestsPage from './pages/Admin/DemoRequestsPage.tsx';
import PrivateWheelPage from './pages/PrivateWheel.tsx';
import { RegistrationSuccessPage } from './pages/RegistrationSuccessPage.tsx';
import { UserHowItWorksPage } from './pages/UserHowItWorksPage.tsx';

const AppContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  overflow-x: hidden;
`;

const Nav = styled.nav`
  padding: 1rem 2rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100vw;
  height: 64px;
  box-sizing: border-box;
`;

const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
`;

const NavLink = styled(Link)`
  color: #4a5568;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #4299e1;
  }
`;

const MainContent = styled.main`
  width: 100vw;
  min-height: calc(100vh - 64px);
  margin-top: 64px;
  overflow-x: hidden;
`;

export const App: React.FC = () => {
  return (
    <Router>
      <Helmet>
        <title>Spin To Connect - Connect with people through a fun and interactive wheel experience</title>
        <meta name="description" content="Spin To Connect - Connect with people through a fun and interactive wheel experience" />
      </Helmet>
      <AppContainer>
        <GlobalStyles />
        <Routes>
          <Route path="/admin/*" element={null} />
          <Route path="*" element={
            <Nav>
              <NavContent>
                <Logo />
                <NavLinks>
                  <NavLink to="/">Home</NavLink>
                  <NavLink to="/how-it-works-user">How It Works</NavLink>
                  <NavLink to="/business">For Business</NavLink>
                </NavLinks>
              </NavContent>
            </Nav>
          } />
        </Routes>
        
        <MainContent>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Wheel />} />
            <Route path="/wheel" element={<Wheel />} />
            <Route path="/wheel/:wheelId" element={<PrivateWheelPage />} />
            <Route path="/business" element={<B2BLanding />} />
            <Route path="/purchase/:planId" element={<PurchasePage />} />
            <Route path="/registration-success" element={<RegistrationSuccessPage />} />
            <Route path="/how-it-works-user" element={<UserHowItWorksPage />} />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Direct dashboard route for debugging */}
            <Route path="/admin/direct-dashboard" element={
              localStorage.getItem('admin_token') ? 
                <AdminDashboard /> : 
                <Navigate to="/admin/login" replace />
            } />
            
            {/* Protected admin routes - AdminLayout is only used here */}
            <Route path="/admin" element={<ProtectedAdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<BusinessDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="wheels" element={<AdminWheels />} />
                <Route path="business-users" element={<AdminBusinessUsers />} />
                <Route path="demo-requests" element={<DemoRequestsPage />} />
                <Route path="admins" element={<AdminsPage />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Route>
            
            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MainContent>
      </AppContainer>
    </Router>
  );
}; 